import { JSONModel, toJSONContext } from '../../models/jsonModel';
import Image, { ImageJSON } from '@/store/models/image';

export enum AdStatus {
  Inactive = 0,
  Active = 1,
}

export interface AdClickPeriod {
  start: string;
  end: string;
  timezone: 'UTC';
}

export interface AdJSON {
  id: number;
  name: string;
  url: string;
  image?: ImageJSON | ImageJSON[];
  products: number[];
  countries: number[];
  status: AdStatus;
  completed?: boolean;
  scheduled?: boolean;
  click_count?: number | null;
  period_click_count?: number | null;
  click_tracking_started_at?: string | null;
  start_date: string;
  end_date: string;
  created_at: string;
  updated_at: string;
}

export default class Ad extends JSONModel<AdJSON> {
  id: number = 0;
  name: string = '';
  url: string = '';
  image?: Image;
  products: number[] = [];
  countries: number[] = [];
  status: AdStatus = AdStatus.Active;
  completed: boolean = false;
  scheduled: boolean = false;
  clickCount: number | null = null;
  periodClickCount: number | null = null;
  clickTrackingStartedAt: string | null = null;
  startDate: string = '';
  endDate: string = '';
  created_at: string = '';
  updated_at: string = '';

  public fromJSON(json: AdJSON): void {
    this.id = Number(json.id);
    this.name = String(json.name || '');
    this.url = String(json.url || '');
    
    if (json.image) {
      const image = Array.isArray(json.image) ? json.image[0] : json.image;

      if (image) {
        this.image = Image.fromJSON(image);
      }
    }

    this.products = Array.isArray(json.products) ? json.products : [];
    this.countries = Array.isArray(json.countries) ? json.countries : [];
    this.status = Number(json.status) === 0 ? AdStatus.Inactive : AdStatus.Active;
    this.completed = Boolean(json.completed);
    this.scheduled = Boolean(json.scheduled);
    this.clickCount = json.click_count == null ? null : Number(json.click_count);
    this.periodClickCount = json.period_click_count == null ? null : Number(json.period_click_count);
    this.clickTrackingStartedAt = json.click_tracking_started_at || null;
    
    // Format dates for HTML date input
    if (json.start_date) {
      const startDate = new Date(json.start_date);
      this.startDate = startDate.toISOString().split('T')[0];
    } else {
      this.startDate = '';
    }
    
    if (json.end_date) {
      const endDate = new Date(json.end_date);
      this.endDate = endDate.toISOString().split('T')[0];
    } else {
      this.endDate = '';
    }
    
    this.created_at = String(json.created_at || '');
    this.updated_at = String(json.updated_at || '');
  }

  public toJSON(context: toJSONContext = 'server'): AdJSON {
    return {
      id: this.id,
      name: this.name,
      url: this.url,
      image: this.image ? (this.image instanceof Image ? this.image.toJSON(context) : this.image) : undefined,
      products: this.products,
      countries: this.countries,
      status: this.status,
      completed: this.completed,
      scheduled: this.scheduled,
      ...(context === 'clone' ? {
        click_count: this.clickCount,
        period_click_count: this.periodClickCount,
        click_tracking_started_at: this.clickTrackingStartedAt,
      } : {}),
      start_date: this.startDate ? new Date(this.startDate).toISOString() : '',
      end_date: this.endDate ? new Date(this.endDate).toISOString() : '',
      created_at: this.created_at,
      updated_at: this.updated_at,
    };
  }
}
